import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, ClipboardCheck, Phone, Lock, RotateCcw } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import LoginForm from '../auth/LoginForm';
import { addDays, fmtDay, ymd, errMsg } from '../components/porters/porterUi';
import { money } from '../components/staff/StaffProfile';
import { PinGate } from '../components/staff/PinGate';

const MARKS = [
  ['on_time', 'On time', 'bg-green-600 text-white', 'border-green-200 text-green-700'],
  ['late', 'Late', 'bg-amber-500 text-white', 'border-amber-200 text-amber-700'],
  ['sick', 'Sick', 'bg-sky-600 text-white', 'border-sky-200 text-sky-700'],
  ['no_show', 'No show', 'bg-red-600 text-white', 'border-red-200 text-red-700'],
];
const TODAY = () => ymd(new Date());

const Row = ({ r, date, locked, future, authFetch, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const set = async (mark) => {
    setBusy(true);
    try { await authFetch('post', '/admin/register', { staff_type: r.staff_type, staff_id: r.staff_id, date, mark }); onChanged(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const id6 = r.staff_id.slice(0, 6);
  return (
    <div className={`rounded-xl border bg-white p-3 flex flex-wrap items-center justify-end gap-3 ${r.mark ? 'border-slate-200' : 'border-dashed border-slate-300'}`} data-testid={`reg-row-${r.staff_type}-${id6}`}>
      <div className="flex items-center gap-3 flex-1 min-w-[200px]">
        {r.photo ? <img src={r.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
          : <span className="w-10 h-10 rounded-full bg-[color:var(--mv-navy)] text-white font-black inline-flex items-center justify-center">{(r.name || '?').slice(0, 1)}</span>}
        <div className="min-w-0">
          <div className="font-black text-[color:var(--mv-navy)] truncate">{r.name}</div>
          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-2">
            <span className="uppercase font-bold">{r.staff_type}{r.pay_type === 'share' ? ' · revenue share' : ''}</span>
            {r.jobs.length ? <span>{r.jobs.map(j => `${j.time} #${j.ref}`).join(' · ')}</span> : <span>No jobs booked</span>}
            {r.off_label && <span className="font-bold text-red-700">{r.off_label}</span>}
            {r.phone && <a href={`tel:${r.phone}`} className="inline-flex items-center gap-0.5"><Phone className="w-3 h-3" />{r.phone}</a>}
          </div>
        </div>
      </div>
      {(r.mark === 'on_time' || r.mark === 'late') && (
        <div className="text-right text-xs min-w-[110px]" data-testid={`reg-pay-${r.staff_type}-${id6}`}>
          <div className={`font-black ${r.pay < 0 ? 'text-red-700' : 'text-[color:var(--mv-navy)]'}`}>{money(r.pay)}</div>
          {r.share && <div className="text-slate-500">{r.share.pct}% of {money(r.share.revenue)}{r.share.crew_total > 0 ? ` − ${money(r.share.crew_total)} crew` : ''}{r.pay < 0 ? ' (owes the difference)' : ''}</div>}
          {r.paid_by && <div className="text-purple-700 font-bold">Paid by {r.paid_by}</div>}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 items-center">
        {MARKS.map(([m, label, on, off]) => (
          <button key={m} type="button" disabled={busy || locked || future} onClick={() => set(m)}
            className={`h-10 px-3 rounded-md text-xs font-bold border transition-colors disabled:opacity-50 ${r.mark === m ? `${on} border-transparent` : `bg-white ${off} hover:bg-slate-50`}`}
            data-testid={`reg-${m}-${r.staff_type}-${id6}`}>{label}</button>
        ))}
        {r.mark && !locked && (
          <button type="button" disabled={busy} onClick={() => set('clear')} className="h-10 w-10 rounded-md text-slate-400 hover:text-slate-700 inline-flex items-center justify-center" aria-label="Clear" title="Clear" data-testid={`reg-clear-${r.staff_type}-${id6}`}><RotateCcw className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );
};

function RegisterInner() {
  const { authFetch } = useAuth();
  const [date, setDate] = useState(TODAY());
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = useCallback(() => authFetch('get', `/admin/register?date=${date}`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [authFetch, date]);
  useEffect(() => { setD(null); load(); }, [load]);
  const future = date > TODAY();
  const rows = (d?.rows || []).filter(r => filter === 'all' || r.staff_type === filter);
  return (
    <div className="min-h-[60vh] bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin" className="text-sm font-bold text-slate-500 inline-flex items-center gap-1" data-testid="reg-back"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <h1 className="font-display text-3xl font-extrabold text-[color:var(--mv-navy)] flex-1 flex items-center gap-2" data-testid="reg-title">
            <ClipboardCheck className="w-7 h-7 text-[color:var(--mv-orange)]" /> Register <span className="text-base text-slate-500 font-bold">{fmtDay(date)}</span>
          </h1>
          <button type="button" onClick={() => setDate(addDays(date, -1))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Previous day" data-testid="reg-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDate(TODAY())} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-bold" data-testid="reg-today">Today</button>
          <button type="button" onClick={() => setDate(addDays(date, 1))} disabled={date >= TODAY()} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center disabled:opacity-40" aria-label="Next day" data-testid="reg-next"><ChevronRight className="w-4 h-4" /></button>
          <input type="date" value={date} max={TODAY()} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-10 px-2 rounded-md border border-slate-200 bg-white text-sm" data-testid="reg-date" />
        </div>
        <p className="text-sm text-slate-500 mt-1">Mark everyone who came in. <strong>On time</strong> or <strong>Late</strong> = paid for the day. <strong>Sick</strong> or <strong>No show</strong> = not paid. Works on days off too.</p>
        {!d ? <Loader2 className="w-5 h-5 animate-spin mt-6" /> : (
          <>
            {d.locked && <div className="mt-3 rounded-md bg-slate-100 text-slate-700 text-sm font-bold p-3 inline-flex items-center gap-2" data-testid="reg-locked"><Lock className="w-4 h-4" />This pay period is locked — the register can't be changed.</div>}
            <div className="mt-4 grid grid-cols-3 sm:grid-cols-6 gap-2">
              {[['On time', d.counts.on_time, 'text-green-700', 'reg-count-on_time'], ['Late', d.counts.late, 'text-amber-600', 'reg-count-late'], ['Sick', d.counts.sick, 'text-sky-700', 'reg-count-sick'], ['No show', d.counts.no_show, 'text-red-700', 'reg-count-no_show'], ['Not marked', d.counts.unmarked, 'text-slate-500', 'reg-count-unmarked'], ['Wages today', money(d.paid_total), 'text-[color:var(--mv-navy)]', 'reg-paid-total']].map(([l, v, c, t]) => (
                <div key={t} className="rounded-lg bg-white border border-slate-200 p-2.5"><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{l}</div><div className={`font-display text-xl font-black ${c}`} data-testid={t}>{v}</div></div>
              ))}
            </div>
            <div className="mt-4 inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="reg-filter">
              {[['all', 'Everyone'], ['driver', 'Drivers'], ['porter', 'Porters']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setFilter(k)} className={`h-8 px-3 rounded text-xs font-bold ${filter === k ? 'bg-[color:var(--mv-navy)] text-white' : 'text-slate-600'}`} data-testid={`reg-filter-${k}`}>{l}</button>
              ))}
            </div>
            <div className="mt-3 space-y-2">
              {rows.map(r => <Row key={`${r.staff_type}-${r.staff_id}`} r={r} date={d.date} locked={d.locked} future={future} authFetch={authFetch} onChanged={load} />)}
              {rows.length === 0 && <div className="text-sm text-slate-500">No staff yet.</div>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function AdminRegister() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!user) return <LoginForm title="Move Van Ltd · Owner" subtitle="Sign in to fill in the register." expectedRole="owner" />;
  if (user.role !== 'owner') return <div className="min-h-[60vh] flex items-center justify-center text-slate-600">Owners only.</div>;
  return <PinGate title="Daily register"><RegisterInner /></PinGate>;
}
